import { Delete, Get, Post, Put } from '../helpers';
import { DownloadStringValues, PasscodeList, PasscodeListMember } from '@/types/registration.types';
import { MagicLink } from '@/micros/edit-project/grid-areas/main/audiences/types';
import { ManuallyAddedUser, UploadPasscodeListMemberResponse } from '@/types/passcode-list.types';
import { getExportBody, getExportUrl } from '@/micros/edit-project/grid-areas/main/audiences/utils';
import { WorkingPasscodeList } from '@/types/passcode-list.types';

export const GetPasscodeLists = (channel: number) => {
  return Get<PasscodeList[]>({
    path: `/v3/admin/content/passcode-lists/${channel}`,
    authRequired: true,
  });
};

export const GetPasscodeList = (channel: number, passcodeListId: number): Promise<PasscodeList> => {
  return Get<PasscodeList>({
    path: `/v3/admin/content/passcode-lists/${channel}/${passcodeListId}`,
    authRequired: true,
  });
};

export const GetPasscodeListMembersForProject = ({
  channel,
  projectId,
  projectUuid,
  passcodeListId,
  offset,
  limit,
  sort,
  search,
  lists = '',
}: {
  channel: number;
  projectId: number;
  projectUuid: string;
  passcodeListId: number;
  offset: number;
  limit: number;
  sort: string;
  search: string;
  lists?: string;
}): Promise<PasscodeListMember[]> => {
  const query = new URLSearchParams({
    offset: offset.toString(),
    limit: limit.toString(),
    search: search || '',
    sort: sort || '',
    lists: lists || '',
  });

  return Get<PasscodeListMember[]>({
    path: `/v3/admin/events/passcode-lists/${channel}/${projectId}/${projectUuid}/${passcodeListId}/members?${query.toString()}`,
    authRequired: true,
  });
};

export const GetPasscodeListMemberCount = (
  passcodeListId: number,
  search?: string
): Promise<{ member_count: number }[]> => {
  return Get<{ member_count: number }[]>({
    path: `/v3/admin/content/passcode-lists/members-count/${passcodeListId}/${search ? search : ''}`,
    authRequired: true,
  });
};

export function GetPasscodeListMembers(
  passcodeListId: number,
  offset: number,
  order: number
): Promise<PasscodeListMember[]> {
  return Get({
    url: `/v3/admin/content/passcode-lists/members/${passcodeListId}/${offset}/${order}`,
  });
}

export function UpdateMasterPasscode(masterPasscode: string, passcodeListId: number): Promise<void> {
  return Put({
    url: `/v3/admin/content/passcode-lists/update-list/master-passcode`,
    body: { passcode: masterPasscode, list: passcodeListId },
    ignoreResponse: true,
  });
}

export function SearchPasscodeListMembers(
  passcodeListId: number,
  offset: number,
  order: number,
  search: string
): Promise<PasscodeListMember[]> {
  return Get({
    url: `/v3/admin/content/passcode-lists/members/${passcodeListId}/${offset}/${order}/${encodeURIComponent(search)}`,
  });
}

export const GetAssociatedEvents = (passcodeListId: number, channel: number): Promise<string[]> => {
  return Post<string[]>({
    path: `/v3/admin/content/associated-events`,
    body: { passcode_list: passcodeListId, channel: channel },
    authRequired: true,
  });
};

export const GetAssociatedEventDetails = (
  eventList: string[],
  channel: number
): Promise<{ name: string; uuid: string; event: number }[]> => {
  if (eventList.length === 0) return Promise.resolve([]);

  return Post({
    path: `/v3/admin/content/associated-events-details`,
    body: { event_list: eventList, channel: channel },
    authRequired: true,
  });
};

export const GetMemberPasscodeLists = (
  channel: number,
  emails: string[]
): Promise<{ passcode_lists: number[]; email: string }[]> => {
  if (!channel || !emails.length) return Promise.resolve([]);
  return Post<{ passcode_lists: number[]; email: string }[]>({
    path: `/v3/admin/content/passcode-lists/passcode-lists-by-email/${channel}`,
    body: { member_emails: emails },
    authRequired: true,
  });
};

export const GetMagicLinksListTemplate = (event_uuid: string): Promise<{ csv: string }> => {
  return Post<{ csv: string }>({
    path: `/v3/admin/events/${event_uuid}/magic-links`,
    authRequired: true,
  });
};

export const UploadPasscodeListMembers = (
  channel: number,
  filename: string,
  passcode_list: number,
  type: number,
  url: string,
  manually_added_users?: ManuallyAddedUser[]
): Promise<UploadPasscodeListMemberResponse> => {
  return Post<UploadPasscodeListMemberResponse>({
    path: '/v3/admin/content/passcode-lists/members',
    body: { filename, passcode_list, type, url, channel, manually_added_users },
    authRequired: true,
  });
};

export function AddNewPasscodeListMember(
  list: number | undefined,
  passcode: string,
  email: string,
  firstName: string,
  lastName: string
): Promise<PasscodeListMember> {
  return Post({
    url: `/v3/admin/content/passcode-lists/addmember/`,
    body: {
      list,
      passcode,
      email,
      firstName,
      lastName,
    },
  });
}

export const UploadMagicLinkPasscodeListMembers = (
  channel: number,
  name: string,
  passcode_list: number | undefined,
  type: number,
  url: string,
  manually_added_users: ManuallyAddedUser[],
  project_uuid: string
): Promise<UploadPasscodeListMemberResponse> => {
  return Post<UploadPasscodeListMemberResponse>({
    path: `/v3/admin/events/${project_uuid}/magic-links/upload`,
    body: { name, list_id: passcode_list, type, url, channel, manually_added_users },
    authRequired: true,
  });
};

export const CreateMagicLinkPasscodeList = (
  channel: number,
  passcode_list: WorkingPasscodeList,
  project_uuid: string
): Promise<UploadPasscodeListMemberResponse> => {
  return Post({
    path: `/v3/admin/events/${project_uuid}/magic-links/upload`,
    body: { ...passcode_list, channel: channel },
    authRequired: true,
  });
};

export const CreateNoMemberPasscodeList = (
  channel: number,
  passcode_list: WorkingPasscodeList
): Promise<UploadPasscodeListMemberResponse> => {
  return Post({
    path: '/v3/admin/content/passcode-lists/no-members',
    body: { ...passcode_list, channel: channel },
    authRequired: true,
  });
};

export const CreatePasscodeList = (
  channel: number,
  passcode_list: WorkingPasscodeList
): Promise<UploadPasscodeListMemberResponse> => {
  return Post({
    path: '/v3/admin/content/passcode-lists',
    body: { ...passcode_list, channel: channel },
    authRequired: true,
  });
};

export const GenerateMagicLinksForList = (
  channel: number,
  audienceList: number,
  eventReturnFilter?: string
): Promise<PasscodeListMember[]> => {
  return Post<PasscodeListMember[]>({
    path: `/v3/admin/channel/${channel}/magic-links/${audienceList}/generate`,
    body: { event_return_filter: eventReturnFilter },
    authRequired: true,
  });
};

export const GenerateMagicLinksForEvent = (
  channel: number,
  audienceList: number,
  eventUuid: string,
  passcodeListType: number,
  selectedMembers?: PasscodeListMember[]
): Promise<PasscodeListMember[]> => {
  return Post<PasscodeListMember[]>({
    path: `/v3/admin/events/magic-links/generate`,
    body: {
      channel,
      audience_list: audienceList,
      event_uuid: eventUuid,
      passcode_list_type: passcodeListType,
      selectedMembers: selectedMembers,
    },
    authRequired: true,
  });
};

export const SendUnsentMagicLinkRegistrationEmails = (
  project_uuid: string,
  passcode_list: number
): Promise<{ success: boolean; message: string }> => {
  return Post<{ success: boolean; message: string }>({
    path: `/v3/admin/send-now-emails/send-magic-link/${project_uuid}`,
    body: { passcodeListId: passcode_list },
    authRequired: true,
  });
};

export const ResetMagicLinkEmailStatus = (
  projectUuid: string,
  passcode_list: number
): Promise<{ success: boolean }> => {
  return Post<{ success: boolean }>({
    path: `/v3/admin/send-now-emails/reset-magic-link-send-status/${projectUuid}`,
    body: { passcodeListId: passcode_list },
    authRequired: true,
  });
};

export const InvalidateMagicLinksForEvent = (
  passcode_list: number,
  project_uuid: string,
  member_ids: number[]
): Promise<MagicLink[]> => {
  return Post<MagicLink[]>({
    path: '/v3/admin/events/magic-links/invalidate',
    body: { list_id: passcode_list, member_ids, event_uuid: project_uuid },
    authRequired: true,
  });
};

export const DeletePasscodeListMembers = (
  member_ids: number[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return Delete<any>({
    path: '/v3/admin/content/passcode-lists/removemember',
    body: { passcodeListMembers: member_ids },
    authRequired: true,
  });
};

export const HandleDownloadCSV = (
  list: PasscodeList,
  downloadType: DownloadStringValues,
  selectedMembers?: PasscodeListMember[],
  projectUuid: string | null = null,
  dmaID?: string | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return Post<any>({
    path: getExportUrl(list, downloadType),
    body: getExportBody(list, downloadType, selectedMembers, projectUuid, dmaID),
    authRequired: true,
  });
};

export const GetMagicLinksForMembers = (
  channel: number,
  list_id: number
): Promise<{ members: PasscodeListMember[] }> => {
  return Get<{ members: PasscodeListMember[] }>({
    path: `/v3/admin/events/${channel}/${list_id}/magic-links`,
    authRequired: true,
  });
};

export const GetMagicLinkRegistrationsAllInvitesSentStatus = (list_id: number): Promise<{ allSent: boolean }> => {
  return Get<{ allSent: boolean }>({
    path: `/v3/admin/events/magic-links/${list_id}/all-sent-status`,
    authRequired: true,
  });
};

export const RemoveDuplicateMembers = (list: number, channel: number): Promise<{ deleted: boolean }> => {
  return Delete<{ deleted: boolean }>({
    path: '/v3/admin/content/passcode-lists/remove-duplicate-members',
    body: { list, channel },
    authRequired: true,
  });
};

export const DeletePasscodeList = (
  channel: number,
  list: PasscodeList
): Promise<{ listErrorMessage?: string; listErrorData?: string; listRemoved?: PasscodeList }> => {
  return Put({
    path: '/v3/admin/content/passcode-lists/delete',
    body: { list, channel },
    authRequired: true,
  });
};

export async function GetPasscodeListEncrypt(
  channel: number,
  passcodelist: number
): Promise<{ encryptedString: string }> {
  return Get({ url: `/v3/admin/content/passcode-lists/encrypt/${channel}/${passcodelist}` });
}
